import {
  Business as BusinessIcon,
  SettingsInputComponent as SettingsInputComponentIcon
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React from 'react';
import {
  ArrayField,
  Button,
  Create,
  Datagrid,
  Edit,
  EditButton,
  FunctionField,
  Labeled,
  NumberField,
  NumberInput,
  ReferenceInput,
  SelectInput,
  Show,
  SimpleShowLayout,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
  useGetRecordId
} from 'react-admin';
import { Link } from 'react-router-dom';

import DeleteSpaceDeviceButton from './DeleteSpaceDeviceButton';
import { handleRelated } from './utils';

const defaultRoomControls = {
  roomControls: {
    centreWallWidth: 1,
    floorWidth: 1,
    floorCentreGap: 0,
    floorXOffset: 0,
    physicalAspectRatio: [],
    surfaceDivisions: 1,
  },
};

const formatAspectRatios = value =>
  Array.isArray(value) ? value.join(', ') : value ?? '';

const parseAspectRatios = value => {
  if (typeof value !== 'string') return value;
  return value
    .replace(/[[\]]/g, '')
    .split(',')
    .map(part => part.trim())
    .filter(part => part !== '')
    .map(Number)
    .filter(num => !Number.isNaN(num));
};

export const SpaceShow = props => {
  const recordId = useGetRecordId();
  return (
    <Show actions={<SpaceShowActions />} {...props}>
      <SimpleShowLayout>
        <TextField source="name" />
        <TextField source="layout.name" />
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Desktop Settings</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%' }}>
          <Labeled label="Centre wall width"><NumberField source="roomControls.centreWallWidth" /></Labeled>
          <Labeled label="Floor width"><NumberField source="roomControls.floorWidth" /></Labeled>
          <Labeled label="Floor centre gap"><NumberField source="roomControls.floorCentreGap" /></Labeled>
          <Labeled label="Floor X offset"><NumberField source="roomControls.floorXOffset" /></Labeled>
          <Labeled label="Physical aspect ratios"><FunctionField render={record => { const v = record?.roomControls?.physicalAspectRatio; return Array.isArray(v) && v.length > 0 ? v.join(', ') : 'Not set'; }} /></Labeled>
          <Labeled label="Surface divisions"><NumberField source="roomControls.surfaceDivisions" /></Labeled>
        </Box>
        <ArrayField source="devices">
          <Datagrid sort={{ field: 'name', order: 'ASC' }}>
            <TextField source="name" />
            <TextField source="device" />
            <DeleteSpaceDeviceButton spaceId={recordId} />
          </Datagrid>
        </ArrayField>
        <AddDeviceButton />
      </SimpleShowLayout>
    </Show>
  );
};

const SpaceShowActions = () => {
  const record = useRecordContext();
  return (
    <TopToolbar>
      <Button
        color="primary"
        component={Link}
        startIcon={<BusinessIcon />}
        label="Organisation"
        to={`/organisations/${record?.organisation.id}/show`}
      />
      <EditButton />
    </TopToolbar>
  );
};

export const AddDeviceButton = () => {
  const record = useRecordContext();
  return (
    <Button
      variant="contained"
      component={Link}
      to={`/space-devices/create?source={"space": "${record.id}"}`}
      label="Add Device"
      title="Add Device"
    >
      <SettingsInputComponentIcon />
    </Button>
  );
};

export const SpaceEdit = props => (
  <Edit redirect="show" {...props}>
    <SimpleForm defaultValues={defaultRoomControls}>
      <TextInput source="name" />
      <ReferenceInput label="Layout" source="layout" reference="layouts">
        <SelectInput optionText="name" format={handleRelated} />
      </ReferenceInput>
      <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>Desktop Settings</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%' }}>
        <NumberInput source="roomControls.centreWallWidth" />
        <NumberInput source="roomControls.floorWidth" />
        <NumberInput source="roomControls.floorCentreGap" label="Floor Centre Gap" />
        <NumberInput source="roomControls.floorXOffset" />
        <TextInput
          source="roomControls.physicalAspectRatio"
          label="Physical Aspect Ratios"
          helperText="One value per surface, comma-separated (e.g. 1.4, 1.45, 2.3)"
          format={formatAspectRatios}
          parse={parseAspectRatios}
        />
        <NumberInput source="roomControls.surfaceDivisions" />
      </Box>
    </SimpleForm>
  </Edit>
);

export const SpaceCreate = props => (
  <Create redirect="show" {...props}>
    <SimpleForm defaultValues={defaultRoomControls}>
      <ReferenceInput
        label="Organisation"
        source="organisation"
        reference="organisations"
      >
        <SelectInput optionText="name" />
      </ReferenceInput>
      <TextInput source="name" />
      <ReferenceInput label="Layout" source="layout" reference="layouts">
        <SelectInput optionText="name" />
      </ReferenceInput>
      <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>Desktop Settings</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%' }}>
        <NumberInput source="roomControls.centreWallWidth" />
        <NumberInput source="roomControls.floorWidth" />
        <NumberInput source="roomControls.floorCentreGap" label="Floor Centre Gap" />
        <NumberInput source="roomControls.floorXOffset" />
        <TextInput
          source="roomControls.physicalAspectRatio"
          label="Physical Aspect Ratios"
          helperText="One value per surface, comma-separated (e.g. 1.4, 1.45, 2.3)"
          format={formatAspectRatios}
          parse={parseAspectRatios}
        />
        <NumberInput source="roomControls.surfaceDivisions" />
      </Box>
    </SimpleForm>
  </Create>
);

export const SpaceDeviceCreate = props => {
  return (
    <Create
      redirect={(basePath, id, data) => `spaces/${data.id}/show`}
      {...props}
    >
      <SimpleForm>
        <ReferenceInput label="Space" source="space" reference="spaces">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput label="Device" source="device" reference="devices">
          <SelectInput optionText="name" />
        </ReferenceInput>
      </SimpleForm>
    </Create>
  );
};

export const SpaceDeviceEdit = props => (
  <Edit redirect="show" {...props}>
    <SimpleForm>
      <ReferenceInput label="Space" source="space" reference="spaces">
        <TextInput optionText="name" disabled />
      </ReferenceInput>
      <ReferenceInput label="Device" source="device" reference="devices">
        <TextInput optionText="name" disabled />
      </ReferenceInput>
    </SimpleForm>
  </Edit>
);
